import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { discardStream } from '../transaction-builders/buildDiscardTransaction';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';

/**
 * Mutable state container for discard handling.
 * Tracks re-entry guard and last seen session ID.
 */
export interface DiscardViewState {
	isDiscardInProgress: boolean;
	lastSeenSessionId: string | undefined;
}

/**
 * Creates the initial discard view state.
 */
export function createDiscardViewState(): DiscardViewState {
	return {
		isDiscardInProgress: false,
		lastSeenSessionId: undefined,
	};
}

/**
 * Handles discarding streamed content when session is cleared mid-stream.
 *
 * This detects when the AI session was cleared while streaming was in progress,
 * and triggers a discard to restore the original document content.
 *
 * @param view - The ProseMirror editor view
 * @param state - Current streaming plugin state
 * @param discardState - Mutable state container for discard tracking
 */
export function handleDiscard(
	view: EditorView,
	state: PurePmInEditorDirectStreamingPluginState,
	discardState: DiscardViewState,
): void {
	// Guard: Don't re-enter while we're discarding
	if (discardState.isDiscardInProgress) {
		return;
	}

	const sessionContextState = aiSessionContextPluginKey.getState(view.state);
	const decorationState = aiExperienceDecorationPluginKey.getState(view.state);
	// Use either source so reactive discard works when session context has no sessionId (e.g. orchestrator flow)
	const currentSessionId = sessionContextState?.sessionId ?? decorationState?.currentSessionId;

	// Detect if session was cleared while we were actively streaming
	const wasStreaming = state.streamPhase !== 'idle' && state.isStreaming;
	const sessionCleared = discardState.lastSeenSessionId && !currentSessionId;

	// Update tracking before processing
	discardState.lastSeenSessionId = currentSessionId;

	if (wasStreaming && sessionCleared) {
		discardState.isDiscardInProgress = true;
		try {
			discardStream(view, true);
		} catch {
			// Discard failed - continue
		} finally {
			discardState.isDiscardInProgress = false;
		}
	}
}
